import React, { useCallback, useEffect, useRef, useState } from 'react';
import { reportsAPI, doctorAPI } from '../../../services/api';
import { scrollContentToTop } from '../../../utils/scroll';
import PatientSearchPanel, { EMPTY_FORM, hasCriteria } from './PatientSearchPanel';
import PatientProfileView from './PatientProfileView';

// Patient Profile: search for a patient by any detail, then open everything the hospital holds
// against them. Two views, one at a time. The search form and its results are kept here rather
// than in the search panel, so "Back to results" returns to exactly what was on screen.

const PatientProfileReport = () => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const seq = useRef(0);

  useEffect(() => {
    let cancelled = false;
    doctorAPI
      .getAll()
      .then((d) => { if (!cancelled) setDoctors(Array.isArray(d) ? d : []); })
      .catch(() => { if (!cancelled) setDoctors([]); });
    return () => { cancelled = true; };
  }, []);

  const search = useCallback(async () => {
    if (!hasCriteria(form)) return;
    const mine = ++seq.current;
    setLoading(true);
    setError('');
    try {
      const payload = await reportsAPI.searchPatients(form);
      if (seq.current === mine) setResult(payload);
    } catch (e) {
      if (seq.current === mine) {
        setError(e.message || 'The search could not be run');
        setResult(null);
      }
    } finally {
      if (seq.current === mine) setLoading(false);
    }
  }, [form]);

  const clear = () => {
    seq.current += 1;
    setForm(EMPTY_FORM);
    setResult(null);
    setError('');
    setLoading(false);
  };

  // Switching between the search and a profile is a change of view, so it starts at the top.
  useEffect(() => { scrollContentToTop(); }, [openId]);

  if (openId) {
    return (
      <PatientProfileView
        key={openId}
        patientId={openId}
        onBack={() => setOpenId(null)}
        onOpenPatient={(id) => setOpenId(id)}
      />
    );
  }

  return (
    <PatientSearchPanel
      form={form}
      setForm={setForm}
      doctors={doctors}
      result={result}
      loading={loading}
      error={error}
      onSearch={search}
      onClear={clear}
      onOpen={setOpenId}
    />
  );
};

export default PatientProfileReport;
